import React, { useEffect, useMemo, useState } from "react"
import { flattenDeep, at, get, isEmpty } from "lodash"
import { useDispatch } from "react-redux"
import uuid from "uuid"
import { checkIsSp } from "utils/util"
import ClassNames from "classnames"
import { infoTableColumns, tableColumns } from "config/table_columns"
import { MenuPanel, Panel } from "components/organisms/panel"
import MiddleChart from "components/molecules/chart/middleChart"
import { InfoTable } from "components/molecules/table"
import { NewsListContent } from "components/molecules/list/news"
import { RankingPanel } from "components/organisms/panel/ranking"
import { APIResponse } from "redux/apis/types"
import { rankingList } from "components/actions/ranking"
import {
  MarginBalance,
  TrustBalance,
  StockLoanInfo,
} from "components/organisms/panel/trust"
import { Sort } from "utils/sort"
import { changeSymbol } from "redux/ducks/Chart"
import APIs from "redux/apis"
import { NewsList } from "components/organisms/panel/newsList"
import { ROUTE_LIST, SECURITY_TYPE } from "config/const"
import { MainContainer } from "components/molecules/utils/mainContainer"

type Option = {
  id: number | string
  name: string
  type?: string
  rankingTypeNum?: number
}

type TemplateType = {
  rankingData: APIResponse.Res
  changeRanking: Function
  isPhone?: boolean
  isForeign?: boolean
}

//国内株・外国株　ランキング：サマリー表示用
export const RankingSummaryTemplate: React.FC<TemplateType> = props => {
  const summaryRankingType = props.isForeign
    ? Sort.ForeignRankingExchangeFull[props.rankingData.summaryExchange]
        .rankingType
    : Sort.RankingExchangeFull[props.rankingData.summaryExchange].rankingType
  const summaryTypeList: Array<Option> = flattenDeep(
    at(rankingList, summaryRankingType)
  )

  return (
    <MainContainer>
      <RankingPanel
        exchangeTypeAction={"SUMMARY_EXCHANGE"}
        rankingData={props.rankingData}
        changeRanking={props.changeRanking}
        typeList={summaryTypeList}
        exchange={props.rankingData.summaryExchange}
        isForeign={props.isForeign}
      />
    </MainContainer>
  )
}

//国内株・外国株　ランキング：詳細表示用
export const RankingDetailTemplate: React.FC<TemplateType> = props => {
  const [detailsData, changeDetailsData] = useState([])

  const detailsType = get(props.rankingData, "detailsType.type")
  const detailsTypeNum = get(props.rankingData, "detailsType.num")
  const detailsExchange = get(props.rankingData, "detailsExchange")

  const detailsRankingType = props.isForeign
    ? Sort.ForeignRankingExchangeFull[props.rankingData.detailsExchange]
        .rankingType
    : Sort.RankingExchangeFull[props.rankingData.detailsExchange].rankingType
  const detailsTypeList: Array<Option> = flattenDeep(
    at(rankingList, detailsRankingType)
  )

  const dispatch = useDispatch()
  const canvasId = useMemo(() => {
    return uuid()
  }, [])

  useEffect(() => {
    if (isEmpty(props.rankingData) || props.isPhone || isEmpty(detailsType[0]))
      return
    const setChartData = async () => {
      const symbolData = await APIs.SymbolDetail.SymbolDetail({
        SecurityType: get(detailsType[0], "SecurityType"),
        Exchange: get(detailsType[0], "Exchange"),
        Symbol: get(detailsType[0], "Symbol"),
      })
      if (get(symbolData, "Result") === 0) {
        dispatch(changeSymbol(canvasId, get(symbolData, "Symbol")))
        changeDetailsData(get(symbolData, "Symbol"))
      }
    }
    setChartData()
  }, [detailsTypeNum, detailsExchange])

  return (
    <MainContainer className="right-thin-layout ranking-detail-template">
      <div className="contents-block">
        <RankingPanel
          exchangeTypeAction={"DETAILS_EXCHANGE"}
          rankingData={props.rankingData}
          changeRanking={props.changeRanking}
          typeList={detailsTypeList}
          exchange={props.rankingData.detailsExchange}
          isPhone={props.isPhone}
          canvasId={canvasId}
          isForeign={props.isForeign}
          changeDetailsData={changeDetailsData}
        />
      </div>
      {!props.isPhone && (
        <div className="contents-block">
          <div className="contents-nest-block">
            <Panel className="panel-main-index" title="" nonePanelHeader={true}>
              <MiddleChart id={canvasId} />
              <InfoTable
                list={infoTableColumns.indicative}
                data={detailsData}
                className="table-striped table-index-detail"
                wrapperCls="index-detail-table-row"
              />
            </Panel>
            <NewsList route={ROUTE_LIST.DOMESTIC_INDEX} />
          </div>
        </div>
      )}
    </MainContainer>
  )
}

type PropsType = {
  data: Array<APIResponse.Res>
  currentContent: number
  changeContent: Function
  setExchange?: Function
  exchange?: number
  callSymbolDetail?: Function
  contentFlag?: boolean
  changeContentFlag?: Function
  setRefreshFlag?: Function
}

export const TrustTemplate: React.FC<PropsType> = props => {
  const isSp = checkIsSp()
  const menuTab = [
    {
      id: 0,
      name: "証金残",
      component: MarginBalance,
      componentProps: { data: props.data },
    },
    {
      id: 1,
      name: "信用残",
      component: TrustBalance,
      componentProps: { data: props.data },
    },
    {
      id: 2,
      name: "逆日歩",
      component: StockLoanInfo,
      componentProps: {
        tableType: tableColumns.trust.negativeInterest,
        data: props.data,
        setExchange: props.setExchange,
        exchange: props.exchange,
        contentFlag: props.contentFlag,
        changeContentFlag: props.changeContentFlag,
      },
    },
    {
      id: 3,
      name: "株不足",
      component: StockLoanInfo,
      componentProps: {
        tableType: tableColumns.trust.shortage,
        data: props.data,
        setExchange: props.setExchange,
        exchange: props.exchange,
        contentFlag: props.contentFlag,
        changeContentFlag: props.changeContentFlag,
      },
    },
  ]

  return (
    <MainContainer className="domestic-container">
      <MenuPanel
        menu={menuTab}
        active={props.currentContent}
        changeMenu={(id: number) => {
          props.changeContent(id)
          props.setExchange(1)
          props.changeContentFlag(true)
        }}
        className={ClassNames("panel-trust", { sp: isSp })}
        refresh={() => props.setRefreshFlag(true)}
      />
    </MainContainer>
  )
}
